import { useState } from "react"
import { formatDate } from "@utils/formatters"
import { Button } from "@components/ui/button"
import {
  useAccount,
  useGetOfferChangeDetailsQuery,
} from "@apis/accounts/useAccounts"
import type { IAccount, IReward } from "@apis/accounts/types"
import { AccountSelect } from "@components/AccountSelect"
import { CustomOfferForm } from "./CustomOfferForm"
import { Loader2 } from "lucide-react"

type TCustomRepriceToolProps = {
  accounts: IAccount[]
  case_guid: string
}

export const CustomRepriceTool = ({
  accounts,
  case_guid,
}: TCustomRepriceToolProps) => {
  const [showChangeOfferForm, setShowChangeOfferForm] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState(
    String(accounts[0].account_id),
  )

  const getAccountQuery = useAccount(
    selectedAccountId || String(accounts[0].account_id),
  )
  const getOfferChangeDetailsQuery = useGetOfferChangeDetailsQuery(
    selectedAccountId || String(accounts[0].account_id),
  )

  const renderRewards = (rewards: IReward[] | undefined) => {
    return rewards?.length
      ? rewards.map((reward, index) => {
          return (
            <div key={`${reward.reward_description}-${index}`}>
              {reward.reward_description} vests on ${formatDate(reward.vest_dt)}
            </div>
          )
        })
      : "NA"
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:gap-4">
        <AccountSelect
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          setSelectedAccountId={setSelectedAccountId}
        />
        <Button
          onClick={() => setShowChangeOfferForm(!showChangeOfferForm)}
          variant={showChangeOfferForm ? "destructive" : "default"}
        >
          {showChangeOfferForm ? "Cancel" : "Create Custom Offer"}
        </Button>
      </div>

      {showChangeOfferForm && (
        <div className="tw:bordered-container tw:space-y-3">
          <div className="tw:flex tw:flex-col tw:gap-3">
            <p className="tw:text-destructive-dark">
              Accounts are only eligible for sign up bonuses once. If the
              account has already received their bonus, they will not be
              eligible for the new sign up bonus. Any unpaid signup bonuses will
              be forfeited.
            </p>
            <p className="tw:text-destructive-dark">
              Any "accrued" rewards will remain and will be paid out upon the
              vesting date. Any new rewards will start accruing at the new rate.
            </p>

            <div className="tw:bordered-container">
              {getAccountQuery.isPending ||
              getOfferChangeDetailsQuery.isPending ? (
                <Loader2 className="tw:animate-spin tw:justify-center tw:text-center tw:text-gray-500" />
              ) : getOfferChangeDetailsQuery.isError ||
                getAccountQuery.isError ? (
                <div className="tw:text-red-500">
                  There was an error loading rewards for plan. Error:{" "}
                  {getOfferChangeDetailsQuery.error?.message ||
                    getAccountQuery.error?.message}
                </div>
              ) : (
                <div className="tw:grid tw:grid-cols-2 tw:gap-2">
                  <p className="tw:col-span-2 tw:font-bold">
                    Account ID: {selectedAccountId}
                  </p>
                  <div className="tw:space-y-3">
                    <div>
                      <h6>Current Plan</h6>
                      <p>{getAccountQuery.data?.plan_name}</p>
                    </div>
                    <div>
                      <h6>Current Rate</h6>
                      <p>
                        {
                          getOfferChangeDetailsQuery.data?.intro_rate_status
                            .intro_rate_message
                        }
                      </p>
                    </div>
                  </div>

                  <div className="tw:space-y-3">
                    <div>
                      <h6>Current Unearned Plan Bonus(es)</h6>
                      {renderRewards(
                        getOfferChangeDetailsQuery.data?.forfeitable_rewards,
                      )}
                    </div>

                    <div>
                      <h6>Current Earned Rewards</h6>
                      {renderRewards(
                        getOfferChangeDetailsQuery.data?.earned_rewards,
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <CustomOfferForm
            account_id={selectedAccountId}
            case_guid={case_guid}
            setShowChangeOfferForm={setShowChangeOfferForm}
          />
        </div>
      )}
    </div>
  )
}
